import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Drawer,
  IconButton,
  Box,
  Chip,
  useMediaQuery,
  Divider,
} from "@mui/material";
import "./Work.css";
import * as THREE from "three";
import ProjectMedia from "./ProjectMedia";
import CloseIcon from "@mui/icons-material/Close";

import contract_gif from "../src/assets/contracts/contract_gif.gif";
import contract_gif2 from "../src/assets/contracts/contract_gif2.gif";
import contract_gif3 from "../src/assets/contracts/contract_gif3.gif";
import contract_gif4 from "../src/assets/contracts/contract_4.png";
import contract_gif5 from "../src/assets/contracts/contract_gif5.gif";

import nyshex_image from "../src/assets/nyshex/nyshex.webp";
import nyshex_image2 from "../src/assets/nyshex/nyshex2.webp";
import nyshex_image3 from "../src/assets/nyshex/nyshex3.webp";
import nyshex_image4 from "../src/assets/nyshex/nyshex4.webp";

import actionface_image2 from "../src/assets/actionface/actionface2.gif";
import actionface_image from "../src/assets/actionface/actionface.jpg";

import intel_gif from "../src/assets/intel/intel_vid.gif";
import intel_gif2 from "../src/assets/intel/intel2.png";
import intel_gif3 from "../src/assets/intel/intel3.jpg";
import intel_gif4 from "../src/assets/intel/intel4.jpg";
import intel_gif5 from "../src/assets/intel/intel5.webp";

import game_dev_gif from "../src/assets/gamedev/game_dev.gif";
import game_dev1 from "../src/assets/gamedev/game_dev7.gif";
import game_dev2 from "../src/assets/gamedev/gamedev_3.gif";
import game_dev3 from "../src/assets/gamedev/gamedev_4.gif";

import stream_chat from "../src/assets/streamerchat/desktop.png";

import parlay_image from "../src/assets/parlaybot/parlay1.png";
import parlay_image2 from "../src/assets/parlaybot/parlay2.png";
import parlay_image3 from "../src/assets/parlaybot/parlay3.png";
import parlay_image4 from "../src/assets/parlaybot/parlay4.png";
import parlay_image5 from "../src/assets/parlaybot/parlay5.png";
import parlay_image6 from "../src/assets/parlaybot/parlay6.png";

import ohNo_image from "../src/assets/ohnotv/Ohno.png";
import { styled } from "@mui/system";
import ParticleComponent from "./ParticleComponent.js";
import FOG from "vanta/dist/vanta.fog.min.js";

const StyledDescription = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  "& p": {
    marginBottom: theme.spacing(2),
  },
}));

const HighlightText = styled(Typography)(({ theme }) => ({
  fontWeight: "bold",
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(1),
}));

const ProjectDescription = ({ description }) => {
  // Split the description into paragraphs
  const paragraphs = description.split("\n\n");

  return (
    <StyledDescription>
      <HighlightText variant="h6">Project Overview</HighlightText>
      {paragraphs.map((paragraph, index) => (
        <React.Fragment key={index}>
          <Typography variant="body1" paragraph>
            {paragraph}
          </Typography>
          {index < paragraphs.length - 1 && <Divider sx={{ my: 2 }} />}
        </React.Fragment>
      ))}
    </StyledDescription>
  );
};

const techColors = {
  // Frontend
  React: "#61dafb",
  "Material-UI": "#007FFF",
  JavaScript: "#F7DF1E",
  HTML: "#E34F26",
  CSS: "#1572B6",
  jQuery: "#0769AD",
  CoffeeScript: "#244776",
  Recharts: "#FF4500",
  Materialize: "#EE6E73",

  // Backend
  "Ruby on Rails": "#CC0000",
  Python: "#3776AB",
  Flask: "#000000",
  "Node.js": "#339933",
  MySQL: "#4479A1",
  PostgreSQL: "#336791",

  // Full Stack
  "REST APIs": "#00ACD7",
  WebSockets: "#005A9C",

  // DevOps & Tools
  Git: "#F05032",

  JIRA: "#0052CC",
  "Agile/Scrum": "#FF9A00",
  Linux: "#FCC624",

  // Testing
  RSpec: "#990000",
  Jest: "#C21325",

  // 3D & Graphics
  "Three.JS": "#F00000",
  WebGL: "#990000",
  Maya: "#8DB600",

  // Game Development
  Unity: "#000000",
  "C#": "#239120",

  // Cloud & Services
  "AWS Polly": "#FF9900",

  // Blockchain
  Solidity: "#363636",
  Blockchain: "#181717",
  NFT: "#A6A9AA",
  Crypto: "#FF5A1F",

  // AI & Machine Learning
  ChatGPT: "#74AA9C",
  "Machine Learning": "#FF6F00",

  // Miscellaneous
  HAML: "#ECE2A9",
  "Server Hardware Repair": "#B8860B",
  "Motherboard Replacement": "#B8860B",
  "Fiber Connection Management": "#B8860B",
  Photoshop: "#31A8FF",
  "Real-time motion capture": "#32CD32",
  "AI programming": "#74AA9C",
  "2D game development": "#FFD700",
  "Multiplayer networking": "#6495ED",
  "Game scripting": "#DAA520",
};

const TechStack = ({ tech }) => {
  return (
    <Box className="flex flex-wrap gap-2">
      <Chip
        key={tech}
        label={tech}
        className="font-semibold text-sm"
        style={{
          backgroundColor: techColors[tech] || "#6b7280", // default color if not found
          color: ["JavaScript", "HTML", "CSS"].includes(tech) ? "#000" : "#fff",
        }}
      />
    </Box>
  );
};

const workProjects = [
  {
    title: "NYSHEX",
    description:
      "Developed interactive tools for the shipping industry using React, handling nested data, time series data, and designing robust endpoints. Engineered dynamic tables, drawers, and calendars to manage complex shipping data, ensuring seamless data integration and user-friendly interfaces.",
    image: nyshex_image,
    images: [nyshex_image, nyshex_image2, nyshex_image3, nyshex_image4],
    detailsImage: nyshex_image,
    detailedDescription:
      "At NYSHEX, I spent over two years engineering dynamic tables, drawers, and calendars specifically designed to handle nested information and time series data for the shipping industry. Using React and Material-UI, I developed scalable software solutions that addressed complex challenges within the Non-Vessel Operating Common Carrier (NVOCC) space. My role involved focusing on data mapping and resolving data integration issues to ensure that our software could seamlessly interact with various data sources and provide reliable insights.\n\nI collaborated closely with product managers, UX/UI designers, and backend developers to deliver high-quality, responsive, and user-friendly frontend solutions. This collaboration emphasized the importance of user experience and effective communication, ensuring that our tools met the specific needs of our users within the shipping supply chain.\n\nOne of my key achievements was the development of robust endpoints and interactive components that significantly enhanced the digital infrastructure of our clients' business operations. The dashboards and visualizations I engineered played a critical role in streamlining operations and providing actionable insights, thereby contributing to the overall efficiency and effectiveness of the shipping supply chain.",
    techStack: [
      "React",
      "Material-UI",
      "JavaScript",
      "HTML",
      "CSS",
      "REST APIs",
      "Node.js",
      "JIRA",
      "Agile/Scrum",
    ],
    video: null,
  },

  {
    title: "Action Face",
    description:
      "Built a 3D customizer using Ruby on Rails and Three.JS, allowing users to create and customize 3D face scans and miniatures directly in the browser. Developed new features and enhancements from UI/UX design documents and collaborated with QA to ensure high application quality and performance.",
    image: actionface_image,
    images: [actionface_image, actionface_image2],
    video: null,
    detailsImage: "https://via.placeholder.com/600x400",
    detailedDescription:
      "At Action Face, I was instrumental in building a 3D customizer using Ruby on Rails and Three.JS, which allowed users to create and customize 3D face scans and miniatures directly in the browser. I developed new features and enhancements based on UI/UX design documents, utilizing Ruby on Rails, HAML, and JavaScript. My collaboration with QA and team members was key in resolving bugs and addressing user requests, ensuring the application maintained high quality and performance.\n\nOne of my notable contributions was creating dynamic shaders for 3D object customization using Three.JS. This feature significantly enhanced the visual effects and overall user experience for customizing face scans from mobile devices. The project not only showcased my technical skills but also demonstrated my ability to drive company growth through technological innovation, evidenced by Action Face's recent acquisition.",
    techStack: [
      "Ruby on Rails",
      "Three.JS",
      "HAML",
      "JavaScript",
      "HTML",
      "CSS",
      "WebGL",
      "PostgreSQL",
      "RSpec",
      "Git",
    ],
  },

  {
    title: "Intel",
    description:
      "Managed a large volumetric capture system for live sports and studio events, ensuring high-quality results under pressure. Worked with highly technical systems, collaborated across teams, and mentored new members.",
    image: intel_gif,
    images: [intel_gif3, intel_gif2, intel_gif4, intel_gif5],
    video: [
      "https://www.youtube.com/embed/J7xIBoPr83A?si=Ojr9UK3Wwjsds4i7",
      "https://www.youtube.com/embed/6q-CEN7-XCk?si=cTtbb6h1O-aDwM4I",
    ],
    detailsImage: intel_gif,
    detailedDescription:
      "My role at Intel as a Systems Engineer involved managing a large volumetric capture system for live sports and studio events. This honed my ability to handle complex systems and deliver high-quality results under pressure, providing a solid foundation for collaborative and team-working skills. At Intel, I worked with highly technical systems, collaborated with many outside the technical space, mentored new team members, and built software to improve operations.\n\nI managed system server processes and hardware maintenance to deliver interactive volumetric content to Intel Sports and Intel Studios Business Partners. As a technical lead, I provided support and troubleshooting to maintain fully operational availability. I also engineered and designed system tools to assist in automating system processes, improving efficiency and reliability. Additionally, I trained new team members on system operation procedures, tools, and troubleshooting steps, ensuring they were well-equipped to handle the complex systems.\n\nThis role required a deep understanding of both hardware and software systems, and my contributions were crucial in maintaining the high performance and reliability expected by Intel's partners.",
    techStack: [
      "Python",
      "Linux",
      "Maya",
      "Server Hardware Repair",
      "Motherboard Replacement",
      "Fiber Connection Management",
    ],
  },
  {
    title: "Contracts.org",
    description:
      "Developed a comprehensive digital signature application for creating, signing, and sending contracts. Implemented features such as multiple signatures, drag-and-drop signature placement, and invite codes for service discounts.",
    image: contract_gif,
    images: [contract_gif2, contract_gif3, contract_gif4, contract_gif5],
    detailsImage: "https://via.placeholder.com/600x400",
    detailedDescription:
      "Contracts.org is a full-featured digital signature application that I built solo as a full stack project. The platform enables users to create, sign, and send contracts effortlessly. Users can choose from a variety of templates, allowing for a quick start with standardized contract formats. One of the standout features is the ability to capture multiple digital signatures, which ensures that all parties involved can sign the document electronically.\n\nTo enhance user experience, I implemented a drag-and-drop functionality for signature boxes, making it easy to indicate where signatures are required within the document. This intuitive interface streamlines the signing process and minimizes errors. Additionally, the application supports invite codes, offering users discounts on the service, which can be easily managed through the dashboard.\n\nThe dashboard itself provides a clear view of the contract's status, including invite codes and the various stages of contract completion. I significantly improved the contract generation time by approximately 85% through a meticulous refactoring of the system architecture and RESTful APIs. This optimization greatly enhanced the overall performance of the application.\n\nA key feature of the platform is the digital signature capture, which I implemented using an HTML5 canvas element combined with CoffeeScript and jQuery. This feature allows users to sign documents directly in the browser, ensuring a seamless and secure signing process. Adhering to test-driven development (TDD) principles, I wrote over 100 unit tests for user authentication to ensure a robust and reliable authentication system.\n\nThis project demonstrates my ability to deliver a complete, user-friendly, and high-performance application, leveraging a wide range of technologies to meet complex requirements.",
    techStack: [
      "Ruby on Rails",
      "HTML5",
      "CoffeeScript",
      "jQuery",
      "JavaScript",
      "CSS",
      "RSpec",
      "Git",
      "PostgreSQL",
      "RESTful APIs",
    ],
    video: null,
  },
];

const personalProjects = [
  {
    title: "Parlay.bot",
    description:
      "Developed a web app for visualizing player stats across NFL, NBA, and MLB, enhanced with ChatGPT for creating parlays and machine learning models for performance projections. Users can analyze performance metrics through interactive charts and detailed game play-by-play analysis.",
    image: parlay_image2,
    images: [
      parlay_image2,
      parlay_image3,
      parlay_image4,
      parlay_image5,
      parlay_image6,
      parlay_image,
    ],
    video: ["https://www.youtube.com/embed/ZvB4DAyi8KQ?si=6f2DHvWJ8vjPFD6J"],
    detailsImage: "https://via.placeholder.com/600x400",
    detailedDescription:
      "Parlay.bot is a comprehensive web application designed to visualize player stats across NFL, NBA, and MLB. The platform provides users with the ability to view and analyze performance metrics through interactive and dynamic charts. As the sole developer of this project, I created responsive interfaces using React and integrated data visualizations with Recharts, ensuring a seamless and engaging user experience.\n\nThe backend was built with Python and Flask, leveraging MySQL for robust data management. I integrated various APIs, including Rapid APIs and SportsRadar, to fetch and display up-to-date player statistics. The application also features user authentication, allowing users to filter and personalize their data views securely.\n\nA key feature of Parlay.bot is the integration of ChatGPT as an assistant for creating parlays for game events. Additionally, machine learning models analyze player performance, providing users with projections of players who are expected to perform better or worse than the betting line. This advanced functionality enhances the user's ability to make informed betting decisions.\n\nUsers can go back to each game play-by-play, see detailed charts, lines, and review past and upcoming games. This comprehensive analysis tool supports users in making data-driven decisions for their parlays.\n\nKey highlights of the project include:\n- Created responsive and intuitive user interfaces with React.\n- Integrated dynamic data visualizations using Recharts, providing users with clear and insightful performance metrics.\n- Implemented robust user authentication and data filtering features, ensuring secure and personalized user experiences.\n- Integrated ChatGPT for creating parlays and providing betting assistance.\n- Developed machine learning models to project player performance relative to betting lines.\n- Enabled detailed game play-by-play analysis, chart views, and comprehensive game data review.\n- Optimized performance and ensured efficient data handling, resulting in a smooth and reliable application.\n\nThis project showcases my ability to develop full stack web applications, integrate complex data visualizations, leverage AI and machine learning models, and ensure high performance and security.",
    techStack: [
      "React",
      "Recharts",
      "Materialize",
      "Python",
      "Flask",
      "MySQL",
      "Rapid APIs",
      "SportsRadar",
      "ChatGPT",
      "Machine Learning",
      "JavaScript",
      "CSS",
      "HTML",
      "Git",
    ],
  },

  {
    title: "OhNo.tv",
    description:
      "Developed a streaming web app with blockchain integration for donations via NFT or crypto. OhNo.tv features a main channel with a queue system, personal chat rooms, and unique chat functionalities.",
    image: ohNo_image,
    images: [ohNo_image],
    video: [
      "https://www.youtube.com/embed/32F6a1H3Uqg?si=i4BYR0MZ07-RXLSe",
      "https://www.youtube.com/embed/y_tBaaEEuWM?si=sgwAJo4mvQicB8lt",
      "https://www.youtube.com/embed/moVn8fg9Q6Q?si=kaipOLoEyWX1RTcZ",
      "https://www.youtube.com/embed/kS4WvnAfBr4?si=dKvhRujBF0bo_RwE",
    ],
    detailsImage: "https://via.placeholder.com/600x400",
    detailedDescription:
      "OhNo.tv, dubbed the 'Reddit of livestreaming,' is a comprehensive streaming web app that I developed as a Full-stack Engineer from November 2021 to February 2022. The platform integrates blockchain technology for donations via NFT or crypto, providing a secure and innovative way for streamers to receive support from their audience.\n\nThe application features a main channel with a queue system that determines homepage presence. Viewers can vote to keep the main streamer featured, creating an interactive and dynamic user experience. Each personal chat room supports group chat, queues, or single-person streaming, enhancing the community aspect of the platform.\n\nI developed OhNo.tv using a diverse technology stack, including JavaScript, jQuery, and HTML5 for the frontend, and Ruby on Rails, WebSockets, Solidity, and PostgreSQL for the backend. The integration of WebSockets enabled real-time communication, while Solidity was used to implement blockchain features for secure and efficient handling of crypto transactions and NFT donations.\n\nAdditionally, I implemented a queue system to manage stream appearances on the homepage, ensuring a fair and engaging process for streamers and viewers alike. The platform also includes video ads and campaign functionality, capturing the reach and audience effectively.\n\nKey highlights of the project include:\n- Built a full-stack application using Ruby on Rails and WebSockets.\n- Integrated blockchain features for donations using Solidity.\n- Implemented a queue system to manage stream appearances on the homepage.\n- Ensured secure and efficient handling of crypto transactions and NFT donations.\n- Developed unique chat functionalities, including group chat and queue lineups.\n- Included video ads and campaign functionality to capture reach and audience.\n\nOhNo.tv showcases my ability to develop innovative streaming solutions, leverage blockchain technology, and create engaging user experiences.",
    techStack: [
      "JavaScript",
      "jQuery",
      "HTML5",
      "Ruby on Rails",
      "WebSockets",
      "Solidity",
      "PostgreSQL",
      "Blockchain",
      "NFT",
      "Crypto",
      "Git",
    ],
  },

  {
    title: "SpeechSynth StreamChat",
    description:
      "Developed a web app that allows streamers to assign personalized voices to chatters watching their live streams, enhancing viewer interaction with real-time voice customization and video recommendations.",
    image: stream_chat,
    images: [stream_chat],
    video: ["https://www.youtube.com/embed/zxirSB30W4s?si=cXGN9RxdF2glp0QU"],
    detailsImage: "https://via.placeholder.com/600x400",
    detailedDescription:
      "SpeechSynth StreamChat is an innovative web application that enhances viewer interaction by allowing streamers to assign personalized voices to chatters during their live streams. This project leverages AWS Polly to provide a variety of voices, giving viewers the ability to type commands and change their voice in real-time. This functionality adds a unique and engaging layer to the streaming experience.\n\nThe application supports integration with popular streaming platforms like Twitch and YouTube. Chatters can also recommend YouTube videos to play through commands, further enhancing the interactive experience. This feature allows for a more dynamic and entertaining stream, as viewers can directly influence the content being played.\n\nI developed SpeechSynth StreamChat using a robust technology stack, ensuring a seamless and user-friendly interface. The frontend was built with React and Material-UI, providing a responsive and visually appealing design. The backend, developed with Node.js, handled the REST APIs and real-time data processing, ensuring smooth performance and scalability.\n\nKey highlights of the project include:\n- Integrated AWS Polly to offer a variety of personalized voices for chatters.\n- Enabled real-time voice customization through chat commands.\n- Supported video recommendations from chatters via YouTube commands.\n- Built a responsive and user-friendly interface using React and Material-UI.\n- Ensured smooth and scalable performance with Node.js and REST APIs.\n- Implemented robust data handling and real-time processing to maintain high-quality user interactions.\n\nSpeechSynth StreamChat demonstrates my capability to create user-centric, innovative solutions in the streaming space, enhancing viewer engagement and interaction.",
    techStack: [
      "React",
      "JavaScript",
      "HTML",
      "CSS",
      "REST APIs",
      "Node.js",
      "AWS Polly",
      "Git",
    ],
  },
  {
    title: "Game Dev",
    description:
      "Developed a 2D multiplayer game with real-life movements captured and turned into sprite animations. Implemented game logic, AI behaviors, and interactive features using Unity.",
    image: game_dev_gif,
    images: [game_dev_gif, game_dev1, game_dev2, game_dev3],
    video: ["https://www.youtube.com/embed/alyUmoao0ZQ?si=NjW6Nlqe2txvQMVB"],
    detailsImage: "https://via.placeholder.com/600x400",
    detailedDescription:
      "The Game Dev project started from a Hackathon collaboration with a friend and evolved into a comprehensive 2D multiplayer game. The unique aspect of this game is that it captures real-life movements of streamers performing individual actions, which are then turned into sprite animations. These movements are used for character actions such as attacks (both long-range and short-range) and walk/run animations.\n\nUsing Unity, I built the world and implemented the game logic, ensuring a seamless and engaging gameplay experience. The project involved scripting various game behaviors, including AI search and destroy, 2D world rotation, and multiplayer view. The AI was programmed to seek out and eliminate targets, adding a challenging element to the game.\n\nKey features of the game include:\n- Capturing real people on film and converting their movements into sprite sheets for in-game characters.\n- Adding hit boxes for accurate detection of attacks, both short and long-range.\n- Developing scripts in Unity to manage game behaviors such as world rotation, AI behaviors, and player interactions.\n- Implementing a points system where players earn points by reaching specific zones, which are animated as donations.\n\nThis project showcases my ability to integrate real-life motion capture into a game, create interactive and dynamic game elements, and develop a full-featured multiplayer game using Unity.",
    techStack: [
      "Unity",
      "C#",
      "Photoshop (for sprite sheet creation)",
      "Real-time motion capture",
      "AI programming",
      "2D game development",
      "Multiplayer networking",
      "Game scripting",
    ],
  },
];

const additionalProjects = [
  // {
  //   title: "Mockups",
  //   description: "Various design mockups for web and mobile applications.",
  //   image: cakepop_gif,
  //   images: [
  //     nyshex_image,
  //     "https://via.placeholder.com/600x400/1",
  //     "https://via.placeholder.com/600x400/2",
  //     "https://via.placeholder.com/600x400/3",
  //   ],
  //   detailsImage: "https://via.placeholder.com/600x400",
  //   detailedDescription:
  //     "More detailed information about the mockups goes here...",
  //   techStack: [],
  // },
  // {
  //   title: "Internet of Things",
  //   description: "Projects involving IoT devices and automation.",
  //   image: iot_gif,
  //   images: [
  //     nyshex_image,
  //     "https://via.placeholder.com/600x400/1",
  //     "https://via.placeholder.com/600x400/2",
  //     "https://via.placeholder.com/600x400/3",
  //   ],
  //   detailsImage: "https://via.placeholder.com/600x400",
  //   detailedDescription:
  //     "More detailed information about IoT projects goes here...",
  //   techStack: [],
  // },
  // {
  //   title: "3D Printing",
  //   description: "Various projects involving 3D printing technology.",
  //   image: printed_gif,
  //   images: [
  //     nyshex_image,
  //     "https://via.placeholder.com/600x400/1",
  //     "https://via.placeholder.com/600x400/2",
  //     "https://via.placeholder.com/600x400/3",
  //   ],
  //   detailsImage: "https://via.placeholder.com/600x400",
  //   detailedDescription:
  //     "More detailed information about 3D printing projects goes here...",
  //   techStack: [],
  // },
  // {
  //   title: "Film",
  //   description: "Various projects involving 3D printing technology.",
  //   image: printed_gif,
  //   images: [
  //     nyshex_image,
  //     "https://via.placeholder.com/600x400/1",
  //     "https://via.placeholder.com/600x400/2",
  //     "https://via.placeholder.com/600x400/3",
  //   ],
  //   detailsImage: "https://via.placeholder.com/600x400",
  //   detailedDescription:
  //     "More detailed information about 3D printing projects goes here...",
  //   techStack: [],
  // },
];

const Work = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const isMobile = useMediaQuery("(max-width:600px)");
  const [selectedImage, setSelectedImage] = useState(
    selectedProject ? selectedProject.detailsImage : null
  );
  const [imageIndex, setImageIndex] = useState(0);
  const [isIterating, setIsIterating] = useState(true);

  const vantaRef = useRef(null);

  useEffect(() => {
    let vantaEffect = null;
    vantaEffect = FOG({
      el: vantaRef.current,
      THREE: THREE,
      mouseControls: true,
      touchControls: true,
      gyroControls: false,

      highlightColor: 0xf0b01,
      midtoneColor: 0x69d82,
      baseColor: 0x0,
      speed: 3,
    });

    return () => {
      if (vantaEffect) vantaEffect.destroy();
    };
  }, []);

  useEffect(() => {
    if (!selectedProject) return;

    setSelectedImage(selectedProject.detailsImage);
    setImageIndex(0);
    setIsIterating(true);
  }, [selectedProject]);

  useEffect(() => {
    if (!selectedProject) return;

    if (drawerOpen && isIterating) {
      const interval = setInterval(() => {
        setImageIndex(
          (prevIndex) => (prevIndex + 1) % selectedProject.images.length
        );
      }, 1200);

      return () => clearInterval(interval);
    }
  }, [drawerOpen, isIterating, selectedProject]);

  useEffect(() => {
    if (!selectedProject) return;

    setSelectedImage(selectedProject.images[imageIndex]);
  }, [imageIndex, selectedProject]);

  const handleOpenDrawer = (project) => {
    setSelectedProject(project);
    setSelectedImage(project.detailsImage);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedProject(null);
  };

  const renderProjects = (projects) =>
    projects.map((project, index) => (
      <Grid item xs={12} sm={6} md={6} lg={6} xl={3} key={index}>
        <Card
          style={{
            backgroundColor: "rgba(240, 240, 240, 0.3)",
            backdropFilter: "blur(10px)",

            color: "black",
          }}
          onClick={() => handleOpenDrawer(project)}
        >
          <CardMedia
            component="img"
            height="200"
            image={project.image}
            alt={project.title}
          />
          <CardContent
            style={{
              backgroundColor: "rgba(240, 240, 240, 0.8)",
              backdropFilter: "blur(10px)",
              padding: "20px",
              color: "black",
            }}
          >
            <Typography variant="h5">{project.title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {project.description}
            </Typography>
            <Box mt={2}>
              <Typography variant="subtitle1">Tech Stack:</Typography>
              <Box display="flex" flexWrap="wrap">
                {project.techStack.map((tech, index) => (
                  <TechStack tech={tech} />
                ))}
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    ));

  return (
    <>
      <div style={{ position: "absolute", top: 0, zIndex: 0 }}>
        <ParticleComponent width="50%" height="50%" />
      </div>
      <Box style={{ position: "relative" }} className="work-info">
        <Box ref={vantaRef} className="work-container">
          <Box>
            <Typography
              variant="h4"
              gutterBottom
              style={{
                color: "white",
              }}
            >
              Work
            </Typography>
            <Typography
              variant="h6"
              gutterBottom
              style={{
                color: "white",
                backgroundColor: "rgba(47, 79, 79, 0.8)",
                backdropFilter: "blur(10px)",
                borderRadius: "5px",
                padding: "10px",
                width: "fit-content",
              }}
            >
              Professional Experiences
            </Typography>
            <Grid container spacing={4}>
              {renderProjects(workProjects)}
            </Grid>
            <Typography
              variant="h6"
              gutterBottom
              style={{
                marginTop: "20px",
                color: "white",
                backgroundColor: "rgba(47, 79, 79, 0.8)",
                backdropFilter: "blur(10px)",
                borderRadius: "5px",
                padding: "10px",
                width: "fit-content",
              }}
            >
              Projects
            </Typography>
            <Grid container spacing={4}>
              {renderProjects(personalProjects)}
            </Grid>
          </Box>
        </Box>
        <Box>
          <Box>
            <Typography variant="h6" gutterBottom style={{ marginTop: "20px" }}>
              Additional Work
            </Typography>
            <Grid container spacing={4}>
              {renderProjects(additionalProjects)}
            </Grid>
          </Box>
        </Box>

        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={handleCloseDrawer}
          PaperProps={{
            sx: {
              width: isMobile ? "80%" : "50%",
            },
          }}
        >
          <Box sx={{ padding: 2 }} role="presentation">
            <IconButton
              onClick={handleCloseDrawer}
              sx={{ position: "fixed", top: 0, right: 0 }}
            >
              <CloseIcon />
            </IconButton>
            {selectedProject && (
              <>
                <Typography variant="h5" gutterBottom>
                  {selectedProject.title}
                </Typography>
                <ProjectMedia
                  key={selectedProject.title}
                  project={selectedProject}
                />
                <ProjectDescription
                  description={selectedProject.detailedDescription}
                />

                <Typography variant="h6" gutterBottom>
                  Tech Stack
                </Typography>
                <Box display="flex" flexWrap="wrap" mb={2}>
                  {selectedProject.techStack.map((tech, index) => (
                    <>
                      <TechStack tech={tech} />
                    </>
                  ))}
                </Box>
              </>
            )}
          </Box>
        </Drawer>
      </Box>
    </>
  );
};

export default Work;
